import React, { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Combobox } from '@base-ui-components/react/combobox'
import useStyles from '../styles'

export interface FilterComboboxOption {
  value: string
  label: string
}

interface FilterComboboxProps {
  /** Material symbol name shown in the trigger chip. */
  icon: string
  /** Trigger chip text (e.g. "Source: All Services"). */
  label: string
  options: FilterComboboxOption[]
  /** Values rendered with a check mark in the list. */
  selectedValues: string[]
  /** Fired with the toggled value: an added pick or a removed one. */
  onSelect: (value: string) => void
  searchPlaceholder: string
  emptyText: string
  /**
   * Single-value pickers (date preset) hold exactly one selection and close
   * after a pick; the default multi mode stays open so several options can
   * be toggled in one visit.
   */
  single?: boolean
  /**
   * Free-text mode for the user filter: a query that matches no option gets
   * an explicit "Add" row so arbitrary values can still be committed.
   */
  allowCustomValue?: boolean
  /**
   * Where the selected-value chips are rendered (the shared row under the
   * filters). The chips still live inside this combobox's React tree — the
   * portal only moves their DOM — so Combobox.Chip keeps its Root context.
   */
  chipsContainer?: HTMLElement | null
  /** Text prepended to each chip label (e.g. "Source: "). */
  chipPrefix?: string
}

const FilterCombobox: React.FC<FilterComboboxProps> = ({
  icon,
  label,
  options,
  selectedValues,
  onSelect,
  searchPlaceholder,
  emptyText,
  single = false,
  allowCustomValue = false,
  chipsContainer = null,
  chipPrefix = '',
}) => {
  const classes = useStyles()
  const [query, setQuery] = useState('')

  const labelByValue = useMemo(
    () => new Map(options.map(o => [o.value, o.label])),
    [options],
  )

  const trimmedQuery = query.trim()
  const lowerQuery = trimmedQuery.toLowerCase()
  const showAddItem =
    allowCustomValue &&
    trimmedQuery !== '' &&
    !options.some(
      o =>
        o.label.toLowerCase() === lowerQuery ||
        o.value.toLowerCase() === lowerQuery,
    )

  const items = useMemo(() => {
    const values = options.map(o => o.value)
    return showAddItem ? [...values, trimmedQuery] : values
  }, [options, showAddItem, trimmedQuery])

  const toLabel = (value: string): string => labelByValue.get(value) ?? value

  const handleOpenChange = (open: boolean): void => {
    if (!open) setQuery('')
  }

  const handleMultiChange = (next: string[]): void => {
    const added = next.find(v => !selectedValues.includes(v))
    const removed = selectedValues.find(v => !next.includes(v))
    const changed = added ?? removed
    if (changed != null && changed !== '') onSelect(changed)
    setQuery('')
  }

  const handleSingleChange = (next: string | null): void => {
    if (next != null && next !== '') onSelect(next)
  }

  const children = (
    <>
      <Combobox.Trigger
        className={`${classes.filterChip} ${classes.comboboxTrigger}`}
      >
        <span className={`material-symbols-outlined ${classes.filterChipIcon}`}>
          {icon}
        </span>
        <span>{label}</span>
        <span
          className={`material-symbols-outlined ${classes.filterChipArrow}`}
        >
          arrow_drop_down
        </span>
      </Combobox.Trigger>
      <Combobox.Portal>
        <Combobox.Positioner
          className={classes.comboboxPositioner}
          align='start'
          sideOffset={4}
        >
          <Combobox.Popup className={classes.comboboxContent}>
            <div className={classes.comboboxInputRow}>
              <span
                className={`material-symbols-outlined ${classes.comboboxSearchIcon}`}
              >
                search
              </span>
              <Combobox.Input
                className={classes.comboboxInput}
                placeholder={searchPlaceholder}
              />
            </div>
            <Combobox.Empty className={classes.comboboxEmpty}>
              {emptyText}
            </Combobox.Empty>
            <Combobox.List className={classes.comboboxList}>
              {(item: string) => {
                const isKnown = labelByValue.has(item)
                const isSelected = selectedValues.includes(item)
                return (
                  <Combobox.Item
                    key={item}
                    value={item}
                    className={classes.comboboxItem}
                  >
                    <span
                      className={`material-symbols-outlined ${classes.comboboxCheck}`}
                      style={{ opacity: isSelected || !isKnown ? 1 : 0 }}
                    >
                      {isKnown ? 'check' : 'add'}
                    </span>
                    <span className={classes.comboboxItemLabel}>
                      {isKnown ? toLabel(item) : `Add "${item}"`}
                    </span>
                  </Combobox.Item>
                )
              }}
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
      {!single &&
        chipsContainer != null &&
        selectedValues.length > 0 &&
        createPortal(
          <Combobox.Chips className={classes.comboboxChips}>
            {selectedValues.map(value => (
              <Combobox.Chip
                key={value}
                className={classes.traceChip}
                aria-label={`${chipPrefix}${toLabel(value)}`}
              >
                <span
                  className='material-symbols-outlined'
                  style={{ fontSize: 14 }}
                >
                  {icon}
                </span>
                <span
                  className={classes.traceChipLabel}
                  title={toLabel(value)}
                >
                  {chipPrefix}
                  {toLabel(value)}
                </span>
                <Combobox.ChipRemove
                  className={classes.traceChipClear}
                  aria-label={`Remove ${chipPrefix}${toLabel(value)}`}
                >
                  <span
                    className='material-symbols-outlined'
                    style={{ fontSize: 14 }}
                  >
                    close
                  </span>
                </Combobox.ChipRemove>
              </Combobox.Chip>
            ))}
          </Combobox.Chips>,
          chipsContainer,
        )}
    </>
  )

  const sharedProps = {
    items,
    inputValue: query,
    onInputValueChange: setQuery,
    onOpenChange: handleOpenChange,
    itemToStringLabel: toLabel,
    autoHighlight: true,
  }

  if (single) {
    return (
      <Combobox.Root
        {...sharedProps}
        value={selectedValues[0]}
        onValueChange={handleSingleChange}
      >
        {children}
      </Combobox.Root>
    )
  }
  return (
    <Combobox.Root
      {...sharedProps}
      multiple
      value={selectedValues}
      onValueChange={handleMultiChange}
    >
      {children}
    </Combobox.Root>
  )
}

export default FilterCombobox
